import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Plus, Trash2, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  useWebsiteSettings,
  useUpdateWebsiteSetting,
  parseFooterNav,
  type FooterNavLink,
  type FooterNavConfig,
} from "@/hooks/admin/useWebsiteSettings";

const STAGING_URL = "https://staging.tristanoscatering.ca";

function NavLinkRowEditor({
  links,
  onChange,
}: {
  links: FooterNavLink[];
  onChange: (next: FooterNavLink[]) => void;
}) {
  return (
    <div className="space-y-2">
      {links.map((link, i) => (
        <div key={i} className="flex gap-2 items-start">
          <Input
            placeholder="Label"
            value={link.label}
            onChange={(e) => {
              const next = [...links];
              next[i] = { ...next[i], label: e.target.value };
              onChange(next);
            }}
            className="flex-1"
          />
          <Input
            placeholder="/url"
            value={link.url}
            onChange={(e) => {
              const next = [...links];
              next[i] = { ...next[i], url: e.target.value };
              onChange(next);
            }}
            className="flex-1"
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove link"
            onClick={() => onChange(links.filter((_, j) => j !== i))}
            className="text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...links, { label: "", url: "/" }])}
      >
        <Plus className="h-4 w-4 mr-2" /> Add link
      </Button>
    </div>
  );
}

export default function WebsiteContentSettingsPage() {
  const { data: settings, isLoading } = useWebsiteSettings();
  const update = useUpdateWebsiteSetting();

  // CTA banner local state
  const [heading, setHeading] = useState("");
  const [subheading, setSubheading] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");

  // Footer nav local state
  const [footerNav, setFooterNav] = useState<FooterNavConfig>({
    row_1: [],
    row_2: [],
    mobile: [],
  });

  useEffect(() => {
    if (!settings) return;
    setHeading(settings.cta_banner_heading ?? "");
    setSubheading(settings.cta_banner_subheading ?? "");
    setButtonText(settings.cta_banner_button_text ?? "");
    setButtonUrl(settings.cta_banner_button_url ?? "");
    const parsed = parseFooterNav(settings.nav_links_footer);
    setFooterNav({
      row_1: parsed.row_1 ?? [],
      row_2: parsed.row_2 ?? [],
      mobile: parsed.mobile ?? [],
    });
  }, [settings]);

  async function saveCtaBanner() {
    try {
      await Promise.all([
        update.mutateAsync({ key: "cta_banner_heading", value: heading }),
        update.mutateAsync({ key: "cta_banner_subheading", value: subheading }),
        update.mutateAsync({ key: "cta_banner_button_text", value: buttonText }),
        update.mutateAsync({ key: "cta_banner_button_url", value: buttonUrl }),
      ]);
      toast.success("CTA banner updated");
    } catch (e) {
      toast.error((e as Error).message || "Failed");
    }
  }

  async function saveFooterNav() {
    try {
      await update.mutateAsync({
        key: "nav_links_footer",
        value: JSON.stringify(footerNav),
      });
      toast.success("Footer nav updated");
    } catch (e) {
      toast.error((e as Error).message || "Failed");
    }
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">Website Content</h1>
            <p className="text-muted-foreground mt-1">
              Edit the CTA banner and footer navigation that appear on the public site.
            </p>
          </div>
          <Button variant="outline" asChild>
            <a href={STAGING_URL} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" /> View on staging
            </a>
          </Button>
        </div>

        <Tabs defaultValue="cta">
          <TabsList>
            <TabsTrigger value="cta">CTA banner</TabsTrigger>
            <TabsTrigger value="footer">Footer nav</TabsTrigger>
          </TabsList>

          <TabsContent value="cta" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Bottom-of-page CTA banner</CardTitle>
                <CardDescription>
                  The large call-to-action section that appears near the bottom of most public pages.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="c-heading">Heading</Label>
                  <Input
                    id="c-heading"
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    placeholder="Ready to start planning your perfect event?"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="c-sub">Sub-heading</Label>
                  <Textarea
                    id="c-sub"
                    rows={2}
                    value={subheading}
                    onChange={(e) => setSubheading(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="c-btn">Button text</Label>
                    <Input
                      id="c-btn"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder="Get a Quote"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="c-url">Button URL</Label>
                    <Input
                      id="c-url"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      placeholder="/contact"
                    />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={saveCtaBanner} disabled={update.isPending}>
                    {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <Save className="h-4 w-4 mr-2" /> Save CTA banner
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="footer" className="mt-4 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Footer navigation — desktop row 1</CardTitle>
                <CardDescription>
                  First row of links in the footer on desktop. Typically services + menus.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLinkRowEditor
                  links={footerNav.row_1 ?? []}
                  onChange={(next) => setFooterNav({ ...footerNav, row_1: next })}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Footer navigation — desktop row 2</CardTitle>
                <CardDescription>
                  Second row. Typically About, Contact, FAQs, Terms.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLinkRowEditor
                  links={footerNav.row_2 ?? []}
                  onChange={(next) => setFooterNav({ ...footerNav, row_2: next })}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Footer navigation — mobile</CardTitle>
                <CardDescription>
                  The collapsed nav shown on mobile breakpoints.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLinkRowEditor
                  links={footerNav.mobile ?? []}
                  onChange={(next) => setFooterNav({ ...footerNav, mobile: next })}
                />
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={saveFooterNav} disabled={update.isPending}>
                {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" /> Save footer nav
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
